import React, { useCallback, useContext, useMemo, useState } from 'react';
import FieldSettingPanelView from '../view/FieldSettingPanelView';
import {
  UPDATE_FIELD,
  UPDATE_FIELD_ACCESS
} from '../../../store/modules/state/formBuilder';
import { defaultCurrency } from '../../../components/form/Currency';
import { FormBuilderFieldFormContext } from '../FormBuilderFieldForm';
import { FormContext } from '../../../components/form/FormContext';

const FieldSettingPanelEngine = props => {
  const [currency, setCurrency] = useState(
    props.fieldOptions[0] || defaultCurrency
  );
  const [defaultCountry, setDefaultCountry] = useState(
    props.defaultCountry || 'GB'
  );
  const [required, setRequired] = useState(props.isRequired || false);
  const [visible2Admin, setVisible2Admin] = useState(
    props.isAdminOnly || false
  );
  const [readOnly, setReadOnly] = useState(props.isReadOnly || false);

  const { autosaveField } = props;

  const { field } = useContext(FormBuilderFieldFormContext);
  const [currentTooltips, setCurrentTooltips] = useState(
    field.annotations?.tooltips || {}
  );

  const onRequiredToggled = event => {
    const _required = event.target.checked;
    const payload = { required: _required };

    if (_required) {
      payload.readOnly = false;
      setReadOnly(false);
    }

    autosaveField(UPDATE_FIELD, {
      field: payload
    });
    setRequired(!required);
  };

  const onTooltipsToggled = event => {
    const newAnnotations = field.annotations || {};
    const willBeEnabled = event.target.checked;

    if (willBeEnabled && !newAnnotations.tooltips) {
      newAnnotations.tooltips = {};
    } else if (!willBeEnabled && newAnnotations.tooltips) {
      delete newAnnotations.tooltips;
    }
    autosaveField(UPDATE_FIELD, {
      field: {
        annotations: newAnnotations
      }
    });
    setCurrentTooltips(newAnnotations);
  };

  const onAdminOnlyToggled = event => {
    const checked = event.target.checked;

    autosaveField(UPDATE_FIELD_ACCESS, {
      access: checked ? 'admin' : 'user'
    });
    if (checked) {
      setReadOnly(false);
      autosaveField(UPDATE_FIELD, {
        field: { readOnly: false }
      });
    }
    setVisible2Admin(!visible2Admin);
  };

  const onReadOnlyToggled = event => {
    const enableReadOnly = event.target.checked;

    let payload = { readOnly: enableReadOnly };
    if (enableReadOnly) {
      payload.required = false;
      setRequired(false);
    }

    autosaveField(UPDATE_FIELD, {
      field: payload
    });
    setReadOnly(!readOnly);
  };

  const onCurrencyChanged = event => {
    const value = event.target.value;

    // local state -- remember if we change type
    setCurrency(value);

    // persist
    autosaveField(UPDATE_FIELD, {
      field: {
        options: [value]
      }
    });
  };

  const onDefaultCountryChanged = event => {
    const value = event.target.value;

    // local state -- remember if we change type
    setDefaultCountry(value);

    // persist
    autosaveField(UPDATE_FIELD, {
      field: {
        defaultCountry: value
      }
    });
  };

  const onFormBuilderDefaultValueSaved = useCallback(
    value => {
      let valueToSave = value || '';
      if (typeof valueToSave === 'string') {
        valueToSave = valueToSave.trim();
      }
      autosaveField(UPDATE_FIELD, {
        field: {
          defaultValue: {
            properties: {
              value: valueToSave
            }
          }
        }
      });
    },
    [field]
  );

  const onFormBuilderTooltipValueSaved = useCallback(
    (tooltipId, value) => {
      const matchingValue = tooltipId.replace('tooltip-', '');
      const existingAnnotations = field.annotations || {};
      const existingTooltips = existingAnnotations.tooltips || {};
      const existingOptions = field.options || [];
      let newTooltips = {};
      existingOptions.forEach(one => {
        if (one === matchingValue) {
          newTooltips[one] = value;
        } else if (existingTooltips[one]) {
          newTooltips[one] = existingTooltips[one];
        }
      });
      autosaveField(UPDATE_FIELD, {
        field: {
          annotations: {
            tooltips: newTooltips
          }
        }
      });
    },
    [field]
  );

  const isDefaultValueEnabled = useMemo(
    () =>
      field.uiType !== 'currency_conversion' &&
      field.uiType !== 'credas_out_of_scope' &&
      field.uiType !== 'instructional_text' &&
      field.uiType !== 'worldcheck_org' &&
      field.uiType !== 'section_break' &&
      field.uiType !== 'file_upload' &&
      field.uiType !== 'user_lookup' &&
      field.uiType !== 'worldcheck' &&
      field.uiType !== 'thirdfort' &&
      field.uiType !== 'credas' &&
      field.uiType !== 'lookup' &&
      field.uiType !== 'score' &&
      field.uiType !== 'phone',
    [field]
  );

  const isTooltipsEnabled = useMemo(
    () =>
      field.uiType === 'yes_no' ||
      field.uiType === 'confirmation' ||
      field.uiType === 'multiple_choice',
    [field]
  );

  const setOneTooltip = (which, value) => {
    let newCurrentTooltips = { ...currentTooltips };
    newCurrentTooltips[which] = value;
    setCurrentTooltips(newCurrentTooltips);
  };

  const viewProps = {
    onTooltipsToggled,
    onRequiredToggled,
    onAdminOnlyToggled,
    onCurrencyChanged,
    onDefaultCountryChanged,
    onReadOnlyToggled,
    required,
    visible2Admin,
    currency,
    readOnly,
    field,
    isDefaultValueEnabled,
    isTooltipsEnabled,
    currentTooltips,
    setOneTooltip,
    ...props,
    defaultCountry // has to be after ...props because we have a default when there isn't one given
  };
  const mockedFormContextValues = {
    registerField: () => {},
    formId: '',
    isReadOnlyFieldsEditable: true,
    onFormChange: () => {},
    onToggleSectionCollapse: () => {},
    isFormReadOnly: false,
    validateField: () => {},
    onFormBuilderDefaultValueSaved,
    onFormBuilderTooltipValueSaved
  };

  return (
    <FormContext.Provider value={mockedFormContextValues}>
      <FieldSettingPanelView {...viewProps} />
    </FormContext.Provider>
  );
};

export default FieldSettingPanelEngine;
